import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { memo } from 'react';
import DraggableItem from './components/DraggableItem';
import useSidebarData from './hooks/useSidebarData';

const SidebarContainer = memo(({ children, collapsed }) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(45deg, #1a0033, #4b0082, #0074D9, #7FDBFF)',
      backgroundSize: '400% 400%',
      animation: 'gradient 15s ease infinite',
      '@keyframes gradient': {
        '0%': { backgroundPosition: '0% 50%' },
        '50%': { backgroundPosition: '100% 50%' },
        '100%': { backgroundPosition: '0% 50%' },
      },
      transition: 'padding 0.2s ease',
    }}
    data-collapsed={collapsed ? 'true' : 'false'}
  >
    {children}
  </Box>
));

const Sidebar = ({ collapsed = false, onToggleCollapsed }) => {
  const { packages, isLoading, error } = useSidebarData();

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flex={1}
          minHeight={0}
        >
          <CircularProgress size={20} sx={{ color: 'white' }} />
        </Box>
      );
    }

    if (error) {
      return (
        <Box p={collapsed ? 0.75 : 1.25}>
          <Alert severity="error" sx={{ fontSize: '0.72rem', px: collapsed ? 0.5 : 1 }}>
            {collapsed ? '!' : error}
          </Alert>
        </Box>
      );
    }

    const groups = [];
    packages.forEach((pkg) => {
      const family = (pkg.type || 'other').split('/')[0];
      const last = groups[groups.length - 1];
      if (!last || last.family !== family) {
        groups.push({ family, items: [pkg] });
      } else {
        last.items.push(pkg);
      }
    });

    return (
      <List
        dense
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          py: 0.75,
          px: collapsed ? 0.5 : 0.75,
        }}
      >
        {groups.map((group) => (
          <Box key={group.family} component="li" sx={{ listStyle: 'none' }}>
            {!collapsed && (
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.62rem',
                  letterSpacing: '0.14em',
                  fontWeight: 700,
                  px: 0.5,
                  pt: 0.75,
                  pb: 0.25,
                }}
              >
                {group.family.toUpperCase()}
              </Typography>
            )}
            {group.items.map((pkg) => (
              <ListItem key={pkg.id || pkg.type} disableGutters sx={{ px: 0, py: 0.35 }}>
                <Tooltip
                  title={collapsed ? `${pkg.name} — drag to add` : `Drag to add ${pkg.name}`}
                  placement="right"
                >
                  <Box width="100%">
                    <DraggableItem packageData={pkg} collapsed={collapsed} />
                  </Box>
                </Tooltip>
              </ListItem>
            ))}
          </Box>
        ))}
      </List>
    );
  };

  return (
    <SidebarContainer collapsed={collapsed}>
      <Box
        sx={{
          px: collapsed ? 0.35 : 0.5,
          py: 0.55,
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: 0.25,
          minHeight: 36,
        }}
      >
        {!collapsed && (
          <Typography
            component="h2"
            sx={{
              color: 'white',
              fontWeight: 700,
              letterSpacing: '0.12em',
              fontSize: '0.68rem',
              lineHeight: 1.2,
              textTransform: 'uppercase',
              opacity: 0.92,
              pl: 0.5,
              flex: 1,
              minWidth: 0,
            }}
          >
            Packages
          </Typography>
        )}
        <Tooltip
          title={collapsed ? 'Expand packages' : 'Collapse packages'}
          placement="right"
        >
          <IconButton
            size="small"
            aria-label={collapsed ? 'Expand packages sidebar' : 'Collapse packages sidebar'}
            aria-expanded={!collapsed}
            onClick={onToggleCollapsed}
            sx={{
              color: 'rgba(255,255,255,0.92)',
              p: 0.35,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.14)',
              },
            }}
          >
            {collapsed ? (
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            ) : (
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />
      {renderContent()}
    </SidebarContainer>
  );
};

export default Sidebar;
